// patient-screening-service client. Talks to the screening backend (a
// DIFFERENT host from the Grails API), sending the logged-in client's session
// token as X-Auth-Token. Because the screening service resolves that token via
// Grails, every evaluation attributes to the authenticated CLIENT — which is
// the whole point of the self-screening test pathway (ZO-11165): unlike the
// WA3 operator demo, the patient is the caller, so attribution is correct.
//
// The token is read fresh per request from localStorage['user'].token (the
// canonical store set by authentication.service.js on login).

const SCREENING_URL =
  process.env.REACT_APP_PATIENT_SCREENING_URL ||
  "https://patient-screening-service.dev.api.zennya.app";

function authToken() {
  try {
    return JSON.parse(localStorage.getItem("user"))?.token || "";
  } catch {
    return "";
  }
}

async function request(path, { method = "GET", body, params } = {}) {
  const url = new URL(`${SCREENING_URL}${path}`);
  if (params) {
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null) url.searchParams.set(k, String(v));
    });
  }
  const headers = { Accept: "application/json", "X-Auth-Token": authToken() };
  if (body !== undefined) headers["Content-Type"] = "application/json";

  const res = await fetch(url.toString(), {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });

  const text = await res.text();
  const data = text ? JSON.parse(text) : undefined;
  if (!res.ok) {
    const err = new Error(data?.detail || `HTTP ${res.status}`);
    err.status = res.status;
    err.body = data;
    throw err;
  }
  return data;
}

export const ScreeningService = {
  /** Resolve the active template for a catalog service_code. null when none (404). */
  resolveTemplate: async (serviceCode) => {
    try {
      return await request("/api/v1/screening/templates/resolve", {
        params: { service_code: serviceCode },
      });
    } catch (e) {
      if (e.status === 404) return null;
      throw e;
    }
  },
  createSession: (body) =>
    request("/api/v1/screening/sessions", { method: "POST", body }),
  upsertAnswers: (sessionId, answers) =>
    request(`/api/v1/screening/sessions/${sessionId}/answers`, {
      method: "PUT",
      body: { answers },
    }),
  evaluate: (sessionId) =>
    request(`/api/v1/screening/sessions/${sessionId}/evaluate`, {
      method: "POST",
      body: {},
    }),
  /** Current review-aware state for the caller (the logged-in client). */
  getState: (serviceCode, requestProfileId = 0) =>
    request("/api/v1/screening/state", {
      params: { service_code: serviceCode, request_profile_id: requestProfileId },
    }),
  /** TEST-ONLY caller-scoped reset (gated by ENABLE_TEST_RESET on the backend). */
  resetScreening: (serviceCode) =>
    request("/api/v1/screening/test-data", {
      method: "DELETE",
      params: serviceCode ? { service_code: serviceCode } : undefined,
    }),
};

export default ScreeningService;
